/**
 * Partner brand registry.
 *
 * Customers of L30 are partners (NOT generic SaaS tenants — see
 * `project_partner_terminology.md`). Each partner can override the
 * default LATITUDE30 brand identity with their own — wordmark, hero
 * motif, etc. — that's applied across the welcome page, sign-in,
 * the magic-link email header, and the authenticated portal chrome.
 *
 * v1: hardcoded map keyed by tenant slug. Partners with no override
 * fall through to the LATITUDE30 default. v2 will hydrate this from
 * the existing `tenant_branding` table per partner.
 *
 * Canon: docs/enhancements/portal-v1-2026-05.md (Sprint B brand
 * canon update + GoP/Transition33 partner-brand integration).
 *
 * The brand record carries WORDMARK + KIND (which controls the
 * wordmark renderer). Colors stay in the CSS-token system; the
 * brand record doesn't override them v1 (every partner inherits the
 * canonical L30 palette). When a partner needs a distinct palette
 * — e.g. Transition33's gold "33" accent — that's applied via
 * partner-scoped CSS or component-level overrides keyed by `kind`.
 */

import { resolvePortalTenantSlug } from "./portal-tenant-path.js";

export type BrandKind = "latitude30" | "transition33";

export interface PartnerBrand {
  /** Tenant slug (lowercase kebab) this brand applies to. */
  slug: string;
  /**
   * Discriminator the wordmark + welcome-page + email renderers
   * branch on. Add a new value when introducing a new brand.
   */
  kind: BrandKind;
  /**
   * Plain-text wordmark for accessibility / fallback / page title.
   * Renderers may apply per-`kind` styling on top (e.g. Transition33
   * splits `Transition` and `33` into ink + gold halves).
   */
  wordmark: string;
}

/**
 * Default brand for any partner without an explicit override.
 * The `slug: ""` is a sentinel — the registry never returns this
 * directly; it's the resolved value when no match is found.
 */
const DEFAULT_BRAND: PartnerBrand = {
  slug: "",
  kind: "latitude30",
  wordmark: "LATITUDE30",
};

/** Per-partner brand overrides. v1 hardcoded; v2 hydrated from DB. */
const PARTNER_BRANDS: Record<string, PartnerBrand> = {
  gop: {
    slug: "gop",
    kind: "transition33",
    wordmark: "Transition33",
  },
  moip: {
    slug: "moip",
    kind: "transition33",
    wordmark: "Transition33",
  },
  mocc: {
    slug: "mocc",
    kind: "transition33",
    wordmark: "Transition33",
  },
};

/**
 * Resolve a partner brand record. Returns the LATITUDE30 default
 * for unknown / null slugs.
 */
export function brandForPartner(slug: string | null | undefined): PartnerBrand {
  if (!slug) return DEFAULT_BRAND;
  return PARTNER_BRANDS[slug] ?? DEFAULT_BRAND;
}

/**
 * Pull the partner slug from the SPA location. Prefers `/t/<slug>/…`
 * (family hosts), then leaf host, then `gop` dual-mode on the family
 * host without a path.
 *
 * SSR-safe: returns the default brand when `window` is undefined.
 */
export function currentBrand(): PartnerBrand {
  if (typeof window === "undefined") return DEFAULT_BRAND;
  return brandForPartner(resolvePortalTenantSlug());
}
